import moment from 'moment';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { ChevronLeftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Container, Spinner, Toolbar } from '@treams/treams-ui';

import TalentReviewPending from './TalentReviewPending';
import TalentReviewV2 from './TalentReviewV2';
import useTalentReviewPath from './hooks/useTalentReviewPath';

export type TalentReviewSnapshotContext = {
	id: string;
	title: string;
	description?: string;
	visibility: graphql.TalentReviewVisibility;
	submittedAt: string;
	submittedBy: string;
	/** Grid day of the snapshot, formatted for display. */
	localDate: string;
	/** True when the viewer (HR) may correct this snapshot. */
	viewerCanCorrect: boolean;
	/** Activity up to the submission plus this snapshot's own corrections, newest first. */
	activity: graphql.TalentReviewActivityFragment[];
};

export default function TalentReview(props: {
	templateID: string;
	groupID: string;
	localDate?: graphql.Scalars['LocalDate']['input'];
	snapshot?: TalentReviewSnapshotContext;
	grid?: graphql.TalentReviewGrid;
	embedded?: boolean;
	/** Page header rendered inside the grid page's main column. */
	header?: ReactNode;
}) {
	const { t } = useTranslation();
	const path = useTalentReviewPath();
	const localDate = props.localDate ?? moment().format('YYYY-MM-DD');

	const query = graphql.useTalentReviewGridQuery({
		skip: !!props.grid,
		variables: {
			templateID: props.templateID,
			groupID: props.groupID,
			localDate,
		},
	});

	// While switching dates, keep showing the previous grid until the new one
	// has loaded, so avatars can move between the two. A different template is
	// another grid entirely: don't draw its cells, but keep the page around
	// them (header, dates, team sidebar) instead of replacing it with a spinner.
	const previousGrid = query.previousData?.talentReviewGrid as graphql.TalentReviewGrid | undefined;
	const grid =
		props.grid ??
		((query.data?.talentReviewGrid ??
			(previousGrid?.template.id === props.templateID && previousGrid?.group.id === props.groupID
				? previousGrid
				: undefined)) as graphql.TalentReviewGrid | undefined);

	if (!grid) {
		if (previousGrid?.group.id === props.groupID) {
			return (
				<TalentReviewPending
					previous={previousGrid}
					templateID={props.templateID}
					groupID={props.groupID}
					localDate={localDate}
				/>
			);
		}

		return (
			<Flex my={5} justifyContent="center">
				<Spinner />
			</Flex>
		);
	}

	const content = (
		<TalentReviewV2
			// Not keyed on the date: the grid stays mounted while switching days so
			// avatars can animate between the two.
			key={`${props.templateID}:${props.groupID}`}
			grid={grid}
			templateID={props.templateID}
			groupID={props.groupID}
			localDate={localDate}
			snapshot={props.snapshot}
			header={props.header}
		/>
	);

	if (props.embedded) {
		return content;
	}

	return (
		<Container>
			<Box py={4}>
				<Toolbar>
					<Button
						icon={<ChevronLeftIcon />}
						link={path({ groupID: props.groupID })}
						title={t('talentReviews.v2.back')}
						type="secondary"
					/>
				</Toolbar>
				{content}
			</Box>
		</Container>
	);
}
